import balloon_url from '@/assets/balon.svg';
import map_pin from '@/assets/map_pin.svg';
import lora_gate_online from '@/assets/base-station-signal.svg';
import lora_gate_offline from '@/assets/base-station-no-signal.svg';
import constants from "@/consts";
import 'leaflet.markercluster';

// Cache for colored balloon icon URLs
const colored_balloon_cache = {};
let balloon_svg_text = null;

async function get_colored_balloon_url(color) {
    if (colored_balloon_cache[color]) return colored_balloon_cache[color];

    try {
        if (!balloon_svg_text) {
            const response = await fetch(balloon_url);
            balloon_svg_text = await response.text();
        }
        const colored_svg = balloon_svg_text.replace(/#ff3e3b/gi, color);
        const blob = new Blob([colored_svg], {type: 'image/svg+xml'});
        const url = URL.createObjectURL(blob);
        colored_balloon_cache[color] = url;
        return url;
    } catch (e) {
        console.log('Failed to color balloon SVG:', e);
        return balloon_url;
    }
}

export class WindyHandler {

    constructor(balloon_points) {
        this.lora_gate_online_icon = this.create_gateway_icon(constants.LORA_GATE_STATUS.ONLINE);
        this.lora_gate_offline_icon = this.create_gateway_icon(constants.LORA_GATE_STATUS.OFFLINE);
        this.map_pin_icon = this.create_map_pin_icon();
        this.available_pressure_levels = [];
        this.balloon_points = balloon_points;

        this.options = {
            key: constants.WINDY.API_KEY,
            lat: this.balloon_points.length > 0 ? balloon_points.at(-1).latitude : 49,
            lon: this.balloon_points.length > 0 ? balloon_points.at(-1).longitude : 16,
            zoom: constants.WINDY.DEFAULT_ZOOM,
        }

        this.windy_map = null;
        this.windy_api = null;

        // Multi-device: { slug: { path: L.polyline, markers: L.marker[] } }
        this.device_layers = {};
    }

    init_windy_map(gateways, devices_data) {
        let that = this;
        window.windyInit(this.options, async (windyAPI) => {
            const {map} = windyAPI;
            that.windy_api = windyAPI;
            that.windy_map = map;
            that.create_lora_gateway_clusters(gateways);

            for (const device of devices_data) {
                await that.create_device_path(device.slug, device.points, device.color);
            }

            that.windy_api.store.set('lang', 'en');
            this.available_pressure_levels = that.windy_api.store.get('availLevels').map(level => level.replace('h', '')).slice(2);

            const first_with_points = devices_data.find(d => d.points.length > 0);
            if (first_with_points) {
                this.set_closest_pressure_point(first_with_points.points.at(-1).pressure);
            }
        });
    }

    async create_device_path(slug, points, color) {
        if (!points || points.length === 0) return;

        const filtered = points.filter(p => p.latitude != null && p.latitude !== '' && p.longitude != null && p.longitude !== '');
        if (filtered.length === 0) return;

        const track = filtered.map(p => [p.latitude, p.longitude]);
        const path = new window.L.polyline(track, {
            color: color,
            weight: 2.5,
            dashArray: "3 3",
            opacity: 0.9,
        }).addTo(this.windy_map);

        const balloon_icon = await this.create_colored_balloon_icon(color);

        const markers = [];
        filtered.forEach((location, index) => {
            const icon = index === 0 ? balloon_icon : this.create_colored_pin_icon(color);
            let marker = new window.L.marker(
                [location.latitude, location.longitude],
                {icon: icon}
            ).bindPopup(this.generate_balloon_popup_text(location)).addTo(this.windy_map);
            markers.push(marker);
        });

        this.device_layers[slug] = { path, markers, color, slug, points: filtered };
    }

    remove_device_path(slug) {
        const layer = this.device_layers[slug];
        if (!layer) return;
        this.windy_map.removeLayer(layer.path);
        layer.markers.forEach(m => this.windy_map.removeLayer(m));
        delete this.device_layers[slug];
    }

    set_device_visibility(slug, visible) {
        const layer = this.device_layers[slug];
        if (!layer) return;
        if (visible) {
            layer.path.addTo(this.windy_map);
            layer.markers.forEach(m => m.addTo(this.windy_map));
        } else {
            this.windy_map.removeLayer(layer.path);
            layer.markers.forEach(m => this.windy_map.removeLayer(m));
        }
    }

    async update_device_path(slug, points, color) {
        this.remove_device_path(slug);
        await this.create_device_path(slug, points, color);
    }

    async add_point_to_device(slug, new_point, color) {
        const layer = this.device_layers[slug];
        if (!layer) {
            await this.create_device_path(slug, [new_point], color);
            return;
        }

        const latlng = [new_point.latitude, new_point.longitude];
        layer.path.addLatLng(latlng);

        if (layer.markers.length > 0) {
            const old_first = layer.markers[0];
            old_first.setIcon(this.create_colored_pin_icon(color));
        }

        const balloon_icon = await this.create_colored_balloon_icon(color);
        let marker = new window.L.marker(
            latlng,
            {icon: balloon_icon}
        ).bindPopup(this.generate_balloon_popup_text(new_point)).addTo(this.windy_map);
        layer.markers.unshift(marker);
        layer.points.unshift(new_point);
    }

    set_closest_pressure_point(current_pressure) {
        const closest = this.available_pressure_levels.reduce((a, b) => {
            return Math.abs(b - current_pressure) < Math.abs(a - current_pressure) ? b : a;
        });
        this.windy_api.store.set('level', `${closest}h`);
    }

    change_map_center(point) {
        if (this.windy_map != null) {
            this.windy_map.panTo(new window.L.LatLng(point.latitude, point.longitude));
        }
    }

    async create_colored_balloon_icon(color) {
        const url = await get_colored_balloon_url(color);
        return new window.L.icon({
            iconUrl: url,
            iconSize: [54, 54],
            iconAnchor: [0, 37],
            popupAnchor: [18, -17],
        });
    }

    create_map_pin_icon() {
        return new window.L.icon({
            iconUrl: map_pin,
            iconSize: [25, 35],
            iconAnchor: [12, 37],
            popupAnchor: [1, -37],
        });
    }

    create_colored_pin_icon(color) {
        return window.L.divIcon({
            className: 'device-pin-icon',
            html: `<div style="width:10px;height:10px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 3px rgba(0,0,0,0.5);"></div>`,
            iconSize: [10, 10],
            iconAnchor: [5, 5],
            popupAnchor: [0, -8],
        });
    }

    create_gateway_icon(status) {
        return new window.L.icon({
            iconUrl: status === constants.LORA_GATE_STATUS.ONLINE ? lora_gate_online : lora_gate_offline,
            iconSize: [44, 44],
            iconAnchor: [17, 17],
            popupAnchor: [4.5, -10],
        });
    }

    create_lora_gateway_clusters(gate_locations) {
        this.marker_clusters = new window.L.MarkerClusterGroup();
        gate_locations.forEach((gate) => {
            if (gate['location'] !== undefined) {
                this.marker_clusters.addLayer(new window.L.marker(
                    [gate.location.latitude, gate.location.longitude],
                    {icon: gate.online ? this.lora_gate_online_icon : this.lora_gate_offline_icon}
                  ).bindPopup(this.generate_gateway_popup_text(gate))
                );
            }
        });
        this.marker_clusters.addTo(this.windy_map);
    }

    delete_lora_gateway_clusters() {
        this.marker_clusters.clearLayers();
        this.windy_map.removeLayer(this.marker_clusters);
    }

    generate_gateway_popup_text(gate) {
        let leading_zero = (time_entity) => time_entity > 9 ? time_entity : '0' + time_entity;
        let text = `<b>name</b>: ${gate.name}<br>`;
        text += `<b>id</b>: ${gate.id}<br>`;
        let date = new Date(gate.last_seen);
        text += `<b>online</b>: <span style="color:${gate.online ? 'green' : 'red'}"><b>${gate.online}</b></span><br>`;
        let last_seen = `${date.getUTCFullYear()}-${leading_zero(date.getMonth())}-${leading_zero(date.getUTCDate())} `;
        last_seen += `${leading_zero(date.getHours())}:${leading_zero(date.getMinutes())}:${leading_zero(date.getSeconds())}`;
        text += `<b>last seen</b>: ${last_seen}`;
        return text;
    }

    generate_balloon_popup_text(balloon) {
        let text = `<b>time</b>: ${balloon.time}<br>`;
        text += `<b>location</b>: ${Number(balloon.latitude).toFixed(3)} `
        text += `${Number(balloon.longitude).toFixed(3)}`;
        if (balloon.altitude != null) text += ` | ${balloon.altitude} MASL`;
        text += `<br>`;
        if (balloon.temperature != null) text += `<b>temperature</b>: ${balloon.temperature}&#8451;<br>`;
        if (balloon.mcu_temperature != null) text += `<b>mcu temperature</b>: ${balloon.mcu_temperature}&#8451;<br>`;
        if (balloon.vcc != null) text += `<b>battery</b>: ${balloon.vcc} V<br>`;
        if (balloon.f_cnt != null) text += `<b>f_cnt</b>: ${balloon.f_cnt}<br>`;
        if (balloon.cycles_since_boot != null) text += `<b>cycles</b>: ${balloon.cycles_since_boot}`;
        return text;
    }

}
